/**
 * Offline cache for assigned-route / schedule data (#1983/#1986 Phase 2,
 * folds in #1498 + #1858).
 *
 * The PWA's pre-check-in screens load schedule + route data straight from
 * the server (useSchedule -> getCallLogs, CallDetail/SetDetail ->
 * getRouteById). With no local copy, a cellular drop produced the diag
 * #33 "Unable to load call / set" dead-end. These helpers write-through
 * the server response into IDB (visit_singletons) on every successful
 * fetch and hand it back when the network is down, so the renderer always
 * has something to show.
 *
 * Stored in visit_singletons (no schema/DB-version bump) under namespaced
 * keys: `schedule:<YYYY-MM-DD>` and `route:<routeId>`.
 */

import { getSingleton, putSingleton } from './visitDB';

const SCHEDULE_PREFIX = 'schedule:';
const ROUTE_PREFIX = 'route:';
const SET_PREFIX = 'set:';
const STORE_DETAIL_PREFIX = 'storedetail:';

/**
 * Cache a StoreDetail SERVER snapshot for an instant cold-load paint (#2066).
 * The caller MUST strip the nav-derived adoption fields before caching — on
 * hydrate they are re-derived from the CURRENT navigation, never restored from
 * this snapshot, so a stale snapshot can never resume the wrong task.
 */
export async function cacheStoreDetailSnapshot<T>(cacheKey: string, snapshot: T): Promise<void> {
  await putSingleton(`${STORE_DETAIL_PREFIX}${cacheKey}`, snapshot);
}

/** Read a cached StoreDetail server snapshot, or null if none cached. */
export async function getCachedStoreDetailSnapshot<T>(cacheKey: string): Promise<T | null> {
  const value = await getSingleton<T>(`${STORE_DETAIL_PREFIX}${cacheKey}`);
  return value ?? null;
}

/** Cache the week's call logs that back the schedule for a given date. */
export async function cacheSchedule<T>(dateKey: string, callLogs: T): Promise<void> {
  await putSingleton(`${SCHEDULE_PREFIX}${dateKey}`, callLogs);
}

/** Read the cached schedule for a date, or null if none cached. */
export async function getCachedSchedule<T>(dateKey: string): Promise<T | null> {
  const value = await getSingleton<T>(`${SCHEDULE_PREFIX}${dateKey}`);
  return value ?? null;
}

/** Cache a single route's detail (the getRouteById response) by routeId. */
export async function cacheRoute<T>(routeId: number, route: T): Promise<void> {
  await putSingleton(`${ROUTE_PREFIX}${routeId}`, route);
}

/** Read a cached route by routeId, or null if none cached. */
export async function getCachedRoute<T>(routeId: number): Promise<T | null> {
  const value = await getSingleton<T>(`${ROUTE_PREFIX}${routeId}`);
  return value ?? null;
}

const AVAILABLE_CALLS_KEY = 'available-calls';

/** Cache the latest Nearby Calls payload ({calls, total}) for instant repaint (#2058). */
export async function cacheAvailableCalls<T>(payload: T): Promise<void> {
  await putSingleton(AVAILABLE_CALLS_KEY, payload);
}

/** Read the last-cached Nearby Calls payload, or null if none cached. */
export async function getCachedAvailableCalls<T>(): Promise<T | null> {
  const value = await getSingleton<T>(AVAILABLE_CALLS_KEY);
  return value ?? null;
}

const TODAY_SETS_KEY = 'today-sets';

/**
 * Cache the raw pending-sets window backing Today's Sets (#3072). Stored
 * unfiltered so the offline repaint can re-run the day/window filter with the
 * CURRENT date — a stale cache can never surface a set outside its window.
 */
export async function cacheTodaySets<T>(sets: T): Promise<void> {
  await putSingleton(TODAY_SETS_KEY, sets);
}

/** Read the last-cached pending-sets window, or null if none cached. */
export async function getCachedTodaySets<T>(): Promise<T | null> {
  const value = await getSingleton<T>(TODAY_SETS_KEY);
  return value ?? null;
}

/** Cache a single set's detail (the getSetById response) by setId. */
export async function cacheSet<T>(setId: number, set: T): Promise<void> {
  await putSingleton(`${SET_PREFIX}${setId}`, set);
}

/** Read a cached set by setId, or null if none cached. */
export async function getCachedSet<T>(setId: number): Promise<T | null> {
  const value = await getSingleton<T>(`${SET_PREFIX}${setId}`);
  return value ?? null;
}
